// SEQ-199: the optional `grid` block on a widget config, for the `grid` chart
// type (the Profit Pareto table and any other rows-by-months table built from
// the transformation library).
//
// Added as an OPTIONAL block, so schemaVersion stays 1 and every config saved
// before this ticket validates unchanged (pnlWidgetSchema.ts file header, D15).
// Everything here has a default, so `grid: {}` and no `grid` at all both mean
// "the Profit Pareto table".
//
// PURE TYPESCRIPT. It reads only the transforms library (a leaf module), so
// the schema validator and the renderer share one set of rules.

import {
  bandsProblem,
  DEFAULT_PERCENT_BANDS,
  type FormatRule,
} from "../../features/pnlAnalytics/transforms/bands.ts";
import { DEFAULT_PARETO_CONFIG, type ParetoBand } from "../../features/pnlAnalytics/transforms/pareto.ts";
import type { PnlUnit } from "./pnlAnalytics.ts";

/** A measure the grid can show per project and per month. Keys are PnlRollup field names (rollup.ts), so no new fetch is needed. */
export interface GridMeasureDef {
  key: string;
  label: string;
  unit: PnlUnit;
  /** True for a dollar or rate-per-hour figure that Safe Mode masks. */
  money: boolean;
  /** True when the value is a percentage of another figure, so a simple mean of rows is the spreadsheet's average. */
  isPercent: boolean;
}

export const GRID_MEASURES: readonly GridMeasureDef[] = [
  { key: "revenue", label: "Revenue", unit: "currency", money: true, isPercent: false },
  { key: "cost", label: "Cost", unit: "currency", money: true, isPercent: false },
  { key: "profit", label: "Profit", unit: "currency", money: true, isPercent: false },
  { key: "profitPercent", label: "Profit %", unit: "percent", money: false, isPercent: true },
  { key: "patching", label: "Patching", unit: "currency", money: true, isPercent: false },
  { key: "patchingPctRevenue", label: "Patching % revenue", unit: "percent", money: false, isPercent: true },
  { key: "overtime", label: "Overtime", unit: "currency", money: true, isPercent: false },
  { key: "overtimePctRevenue", label: "Overtime % revenue", unit: "percent", money: false, isPercent: true },
];

export const GRID_MEASURE_KEYS: readonly string[] = GRID_MEASURES.map((m) => m.key);

/** Measures the share, rank and category columns can work on: only figures that add up (a sum of percentages means nothing). */
export const GRID_SUMMARY_MEASURE_KEYS: readonly string[] = GRID_MEASURES.filter((m) => !m.isPercent).map((m) => m.key);

export function gridMeasure(key: string): GridMeasureDef | undefined {
  return GRID_MEASURES.find((m) => m.key === key);
}

/** The summary columns the library can add after the months. Total is per chosen measure; the rest work on `summaryMeasure`. */
export type GridSummaryColumn = "total" | "percentOfTotal" | "cumulativePercent" | "rank" | "category";

export const GRID_SUMMARY_COLUMNS: readonly { key: GridSummaryColumn; label: string }[] = [
  { key: "total", label: "Total" },
  { key: "percentOfTotal", label: "% of Total" },
  { key: "cumulativePercent", label: "Cumulative %" },
  { key: "rank", label: "Rank" },
  { key: "category", label: "Category" },
];

export interface PnlGridSort {
  /** A column id from gridModel.ts: "name", "m:<YYYY-MM-01>:<measure>", or "s:total:<measure>" or "s:<summary>". */
  column: string;
  direction: "asc" | "desc";
}

export interface PnlGridConfig {
  /** Measure keys shown for every month, in order. Default profit then profit %. */
  measures?: string[];
  /** Summary columns after the months, in order. Default all five. */
  summary?: GridSummaryColumn[];
  /** The measure rank, % of Total, Cumulative % and Category work on. Default profit. */
  summaryMeasure?: string;
  /** Pareto cut-offs and labels. Default one cut-off at 80 labelled Vital, then Trivial. */
  paretoBands?: ParetoBand[];
  paretoRestLabel?: string;
  /** Keep the first N rows and fold the rest into "Other". Absent means every row. */
  topN?: number;
  /** The averages row at the top. Default on. */
  averageRow?: boolean;
  /** The saved sort. Default Total of the summary measure, descending. */
  sort?: PnlGridSort;
  /** Column ids hidden by the viewer's "Show or hide columns" choice. */
  hiddenColumns?: string[];
  /** Keep the first column in view while scrolling sideways. Default on. */
  freezeFirstColumn?: boolean;
  /** Conditional formatting, per measure. Default: Josh's bands on Profit %. */
  formatRules?: FormatRule[];
}

export const DEFAULT_GRID_MEASURES: readonly string[] = ["profit", "profitPercent"];
export const DEFAULT_GRID_SUMMARY: readonly GridSummaryColumn[] = ["total", "percentOfTotal", "cumulativePercent", "rank", "category"];
export const DEFAULT_GRID_SUMMARY_MEASURE = "profit";
/** The most rows an "Other" fold may keep and the most columns a grid draws. Bounds the table, not the data. */
export const GRID_MAX_TOP_N = 500;
export const GRID_MAX_MEASURES = 4;
export const GRID_MAX_RULES = 8;

export const DEFAULT_GRID_FORMAT_RULES: FormatRule[] = [{ measure: "profitPercent", bands: DEFAULT_PERCENT_BANDS }];

export interface ResolvedGridConfig {
  measures: string[];
  summary: GridSummaryColumn[];
  summaryMeasure: string;
  paretoBands: ParetoBand[];
  paretoRestLabel: string;
  topN: number | null;
  averageRow: boolean;
  sort: PnlGridSort;
  hiddenColumns: string[];
  freezeFirstColumn: boolean;
  formatRules: FormatRule[];
}

/** Fills every default. Never throws: a stored value that no longer fits falls back to the default rather than blanking the widget. */
export function resolveGridConfig(grid: PnlGridConfig | undefined): ResolvedGridConfig {
  const g = grid ?? {};
  const measures = (g.measures ?? []).filter((k) => GRID_MEASURE_KEYS.includes(k));
  const resolvedMeasures = measures.length > 0 ? [...new Set(measures)].slice(0, GRID_MAX_MEASURES) : [...DEFAULT_GRID_MEASURES];
  const summary = (g.summary ?? []).filter((s) => GRID_SUMMARY_COLUMNS.some((c) => c.key === s));
  const summaryMeasure =
    typeof g.summaryMeasure === "string" && GRID_SUMMARY_MEASURE_KEYS.includes(g.summaryMeasure)
      ? g.summaryMeasure
      : DEFAULT_GRID_SUMMARY_MEASURE;
  return {
    measures: resolvedMeasures,
    summary: g.summary === undefined ? [...DEFAULT_GRID_SUMMARY] : [...new Set(summary)],
    summaryMeasure,
    paretoBands: g.paretoBands && g.paretoBands.length > 0 ? g.paretoBands : DEFAULT_PARETO_CONFIG.bands,
    paretoRestLabel: g.paretoRestLabel && g.paretoRestLabel.trim() !== "" ? g.paretoRestLabel : DEFAULT_PARETO_CONFIG.restLabel,
    topN: typeof g.topN === "number" && g.topN > 0 ? Math.min(Math.floor(g.topN), GRID_MAX_TOP_N) : null,
    averageRow: g.averageRow !== false,
    sort: g.sort ?? { column: `s:total:${summaryMeasure}`, direction: "desc" },
    hiddenColumns: g.hiddenColumns ?? [],
    freezeFirstColumn: g.freezeFirstColumn !== false,
    formatRules: g.formatRules ?? DEFAULT_GRID_FORMAT_RULES,
  };
}

/** Structural findings for a `grid` block, as developer strings. Empty means valid. */
export function validateGridConfig(grid: unknown): string[] {
  const errors: string[] = [];
  if (grid === undefined) return errors;
  if (typeof grid !== "object" || grid === null || Array.isArray(grid)) return ["grid must be an object when present"];
  const g = grid as Record<string, unknown>;

  if (g.measures !== undefined) {
    if (!Array.isArray(g.measures) || g.measures.some((m) => typeof m !== "string" || !GRID_MEASURE_KEYS.includes(m))) {
      errors.push(`grid.measures must be a list of ${GRID_MEASURE_KEYS.join(", ")}`);
    } else if (g.measures.length === 0 || g.measures.length > GRID_MAX_MEASURES) {
      errors.push(`grid.measures must hold 1 to ${GRID_MAX_MEASURES} measures`);
    }
  }
  if (g.summary !== undefined) {
    if (!Array.isArray(g.summary) || g.summary.some((s) => !GRID_SUMMARY_COLUMNS.some((c) => c.key === s))) {
      errors.push(`grid.summary must be a list of ${GRID_SUMMARY_COLUMNS.map((c) => c.key).join(", ")}`);
    }
  }
  if (g.summaryMeasure !== undefined && (typeof g.summaryMeasure !== "string" || !GRID_SUMMARY_MEASURE_KEYS.includes(g.summaryMeasure))) {
    errors.push(`grid.summaryMeasure must be one of ${GRID_SUMMARY_MEASURE_KEYS.join(", ")}`);
  }
  if (g.paretoBands !== undefined) {
    const bands = g.paretoBands;
    if (!Array.isArray(bands) || bands.length === 0 || bands.length > 5) {
      errors.push("grid.paretoBands must hold 1 to 5 cut-offs");
    } else {
      for (const band of bands) {
        const b = band as Record<string, unknown> | null;
        if (
          typeof b !== "object" || b === null ||
          typeof b.upToPercent !== "number" || !Number.isFinite(b.upToPercent) || b.upToPercent < 0 || b.upToPercent > 100 ||
          typeof b.label !== "string" || b.label.trim() === "" || b.label.length > 40
        ) {
          errors.push("each grid.paretoBands entry needs upToPercent from 0 to 100 and a label of up to 40 characters");
          break;
        }
      }
    }
  }
  if (g.paretoRestLabel !== undefined && (typeof g.paretoRestLabel !== "string" || g.paretoRestLabel.trim() === "" || g.paretoRestLabel.length > 40)) {
    errors.push("grid.paretoRestLabel must be a label of up to 40 characters");
  }
  if (g.topN !== undefined && (typeof g.topN !== "number" || !Number.isInteger(g.topN) || g.topN < 1 || g.topN > GRID_MAX_TOP_N)) {
    errors.push(`grid.topN must be a whole number from 1 to ${GRID_MAX_TOP_N}`);
  }
  for (const flag of ["averageRow", "freezeFirstColumn"] as const) {
    if (g[flag] !== undefined && typeof g[flag] !== "boolean") errors.push(`grid.${flag} must be a boolean when present`);
  }
  if (g.sort !== undefined) {
    const s = g.sort as Record<string, unknown> | null;
    if (typeof s !== "object" || s === null || typeof s.column !== "string" || s.column.length === 0 || s.column.length > 80) {
      errors.push("grid.sort.column must be a non-empty string");
    } else if (s.direction !== "asc" && s.direction !== "desc") {
      errors.push('grid.sort.direction must be "asc" or "desc"');
    }
  }
  if (g.hiddenColumns !== undefined) {
    if (!Array.isArray(g.hiddenColumns) || g.hiddenColumns.length > 200 || g.hiddenColumns.some((c) => typeof c !== "string" || c.length > 80)) {
      errors.push("grid.hiddenColumns must be a list of column ids");
    }
  }
  if (g.formatRules !== undefined) {
    if (!Array.isArray(g.formatRules) || g.formatRules.length > GRID_MAX_RULES) {
      errors.push(`grid.formatRules must be a list of up to ${GRID_MAX_RULES} rules`);
    } else {
      const seen = new Set<string>();
      for (const rule of g.formatRules) {
        const r = rule as Record<string, unknown> | null;
        if (typeof r !== "object" || r === null || typeof r.measure !== "string" || !GRID_MEASURE_KEYS.includes(r.measure)) {
          errors.push(`each grid.formatRules entry needs a measure from ${GRID_MEASURE_KEYS.join(", ")}`);
          break;
        }
        if (seen.has(r.measure)) {
          errors.push(`grid.formatRules has two rules for ${r.measure}`);
          break;
        }
        seen.add(r.measure);
        const problem = bandsProblem(r.bands);
        if (problem) {
          errors.push(`grid.formatRules ${r.measure}: ${problem}`);
          break;
        }
      }
    }
  }
  return errors;
}
